// Defines message handlers for listening to messages sent by the Nut frontend.
//
// - recording-data: Gather all simulation data needed to create a recording of the app.
// - mouse-data: Get data about the current mouse position.
// - get-detected-error: Get any error detected in the app.

import type {
  IndexedDBAccess,
  LocalStorageAccess,
  NetworkRequest,
  NetworkResponse,
  SimulationData,
  SimulationPacket,
  UserInteraction,
} from "./simulationData";
import type {
  MessageHandlerRequest,
  MessageHandlerRequestMap,
} from "./interface";
import type { DetectedError, DetectedErrorData } from "./detectedError";
import { record } from "rrweb";

export function assert(
  condition: any,
  message: string = "Assertion failed!"
): asserts condition {
  if (!condition) {
    debugger;
    throw new Error(message);
  }
}

export function stringToBase64(inputString: string) {
  if (typeof inputString !== "string") {
    throw new TypeError("Input must be a string.");
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(inputString);

  let str = "";

  for (const byte of data) {
    str += String.fromCharCode(byte);
  }

  return btoa(str);
}

export let getSimulationDataDirect: () => Promise<SimulationData> | undefined;

// Add handlers to the current window.
export async function initializeMessageHandlers() {
  const simulationData: SimulationData = [];
  let numSimulationPacketsSent = 0;

  function pushSimulationData(packet: SimulationPacket) {
    packet.time = new Date().toISOString();
    simulationData.push(packet);
  }

  function pushDetectedError(
    message: string,
    details: string | undefined,
    data: DetectedErrorData
  ) {
    const error: DetectedError = {
      time: new Date().toISOString(),
      message,
      details,
      data,
    };
    pushSimulationData({
      kind: "detectedError",
      detectedError: error,
    });
    console.log("DetectedError", error);
  }

  const startTime = Date.now();

  pushSimulationData({
    kind: "viewport",
    size: { width: window.innerWidth, height: window.innerHeight },
  });
  pushSimulationData({
    kind: "locationHref",
    href: window.location.href,
  });
  pushSimulationData({
    kind: "documentURL",
    url: window.location.href,
  });

  function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }

  function getBackendRequestId(
    responseHeaders: Record<string, string> | undefined
  ): string | undefined {
    return responseHeaders?.["x-replay-request-id"];
  }

  function addNetworkRequest(request: NetworkRequest) {
    pushSimulationData({
      kind: "networkRequest",
      request,
    });
  }

  function getNetworkRequest(requestIndex: number): NetworkRequest | undefined {
    const requestPacket = simulationData.find(p => p.kind === "networkRequest" && p.request.requestIndex === requestIndex);
    if (!requestPacket) {
      return undefined;
    }
    assert(requestPacket.kind === "networkRequest", "Expected networkRequest packet");
    return requestPacket.request;
  }

  function addNetworkResponse(response: NetworkResponse) {
    const request = getNetworkRequest(response.requestIndex);

    if (
      request &&
      response.responseStatus &&
      response.responseStatus >= 400 &&
      !request.url.includes("auth.nut.new")
    ) {
      pushDetectedError(
        "Network request failed",
        `${request.url} failed with status ${response.responseStatus}`,
        {
          kind: "network-request-failed",
          url: request.url,
          requestId: getBackendRequestId(response.responseHeaders),
          responseStatus: response.responseStatus,
          responseBodyBase64: response.responseBodyBase64,
        }
      );
    }

    pushSimulationData({
      kind: "networkResponse",
      response,
    });
  }

  function addTextNetworkResponse(
    requestIndex: number,
    text: string,
    responseStatus: number,
    responseHeaders: Record<string, string>
  ) {
    addNetworkResponse({
      requestIndex,
      responseBodyBase64: stringToBase64(text),
      responseStatus,
      responseHeaders,
    });
  }

  function addInteraction(interaction: UserInteraction) {
    pushSimulationData({
      kind: "interaction",
      interaction,
    });
  }

  function isTextInputElement(
    element: Element
  ): element is HTMLInputElement | HTMLTextAreaElement {
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
  }

  function getInputElementValue(element: Element): string | undefined {
    if (isTextInputElement(element)) {
      return element.value ?? "";
    }
    if (element instanceof HTMLElement && element.isContentEditable) {
      return element.textContent ?? "";
    }
    return undefined;
  }

  function addIndexedDBAccess(access: IndexedDBAccess) {
    pushSimulationData({
      kind: "indexedDB",
      access,
    });
  }

  function addLocalStorageAccess(access: LocalStorageAccess) {
    pushSimulationData({
      kind: "localStorage",
      access,
    });
  }

  // Initialize rrweb recording to capture all DOM mutations
  record({
    emit(event: any) {
      pushSimulationData({
        kind: "rrweb",
        event,
      });
    },
    checkoutEveryNms: 10_000, // Periodically create full snapshots every 10 seconds
  });

  async function getSimulationData(): Promise<SimulationData> {
    //console.log("GetSimulationData", simulationData.length, numSimulationPacketsSent);
    const data = simulationData.slice(numSimulationPacketsSent);
    numSimulationPacketsSent = simulationData.length;

    return data;
  }

  async function handleRequest<T extends MessageHandlerRequest>({
    request,
    payload,
  }: MessageHandlerRequest): Promise<
    MessageHandlerRequestMap[T["request"]]["response"]
  > {
    switch (request) {
      case "recording-data": {
        const data = await getSimulationData();

        const encoder = new TextEncoder();
        const serializedData = encoder.encode(JSON.stringify(data));
        const buffer = serializedData.buffer;

        return buffer;
      }
      case "mouse-data": {
        const { x, y } = payload;
        const element = document.elementFromPoint(x, y);
        assert(element);

        const selector = computeSelector(element);
        const rect = element.getBoundingClientRect();

        return {
          selector,
          width: rect.width,
          height: rect.height,
          x: x - rect.x,
          y: y - rect.y,
        };
      }
    }
    throw new Error("Unknown request type: " + request);
  }

  window.addEventListener("message", async event => {
    if (event.data?.source !== "@@replay-nut") {
      return;
    }

    const response = await handleRequest(event.data.request);
    window.parent.postMessage(
      { id: event.data.id, response, source: "@@replay-nut" },
      "*"
    );
  });

  // Evaluated function to find the selector and associated data.
  function getMouseEventTargetData(event: MouseEvent) {
    assert(event.target);

    const target = event.target as Element;
    const selector = computeSelector(target);
    const rect = target.getBoundingClientRect();

    return {
      selector,
      width: rect.width,
      height: rect.height,

      /*
        * at times event.clientX and event.clientY can be slighly off in relation to the element's position
        * it's possible that this position might lie outside the element's bounds
        * the difference likely comes from a subpixel rounding or hit target calculation in the browser
        * it's possible that we should account for event.width and event.height here but clamping the values to the bounds of the element should be good enough
        */
      x: clamp(event.clientX - rect.x, 0, rect.width),
      y: clamp(event.clientY - rect.y, 0, rect.height),
    };
  }

  function getKeyboardEventTargetData(event: KeyboardEvent) {
    assert(event.target);

    const target = event.target as Element;
    const selector = computeSelector(target);

    return {
      selector,
      key: event.key,
    };
  }

  function computeSelector(target: Element): string {
    const path: string[] = [];
    let current: Element | null = target;

    while (current) {
      const testId = current.getAttribute('data-testid');
      if (testId) {
        path.unshift(`[data-testid="${testId}"]`);
        break;
      }

      if (current.id) {
        path.unshift("#" + CSS.escape(current.id));
        break;
      }

      let selector = current.tagName.toLowerCase();

      const parent = current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children);
        const index = siblings.indexOf(current) + 1;

        if (siblings.filter(el => el.tagName === current!.tagName).length > 1) {
          selector += `:nth-child(${index})`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(" > ");
  }

  window.addEventListener(
    "input",
    (event: Event) => {
      if (!event.target) {
        return;
      }
      const target = event.target as Element;
      const value = getInputElementValue(target);
      if (value === undefined) {
        return;
      }

      const selector = computeSelector(target);
      const inputEvent = event as InputEvent;
      const inputType = inputEvent.inputType;

      addInteraction({
        kind: "input",
        time: Date.now() - startTime,
        selector,
        value,
        inputType,
        data: inputEvent.data ?? undefined,
      });
    },
    { capture: true }
  );

  window.addEventListener(
    "click",
    event => {
      if (event.target) {
        addInteraction({
          kind: "click",
          time: Date.now() - startTime,
          ...getMouseEventTargetData(event),
          ...(event.button && { button: event.button }),
          clickCount: event.detail,
        });
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    "pointermove",
    event => {
      if (event.target) {
        addInteraction({
          kind: "pointermove",
          time: Date.now() - startTime,
          ...getMouseEventTargetData(event),
        });
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    "keydown",
    event => {
      if (event.key) {
        addInteraction({
          kind: "keydown",
          time: Date.now() - startTime,
          ...getKeyboardEventTargetData(event),
        });
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    "scroll",
    event => {
      const target =
        event.target == window.document ? undefined : (event.target as Element);
      const selector = target ? computeSelector(target) : undefined;

      addInteraction({
        kind: "scroll",
        time: Date.now() - startTime,
        selector,
        windowScrollX: window.scrollX,
        windowScrollY: window.scrollY,
        targetScrollX: target?.scrollLeft,
        targetScrollY: target?.scrollTop,
      });
    },
    { capture: true, passive: true }
  );

  function onInterceptedOperation(_name: string) {
    //console.log("InterceptedOperation " + name);
  }

  function interceptProperty(
    obj: object,
    prop: string,
    interceptor: (basevalue: any) => any
  ) {
    const descriptor = Object.getOwnPropertyDescriptor(obj, prop);
    assert(descriptor?.get, "Property must have a getter");

    let interceptValue: any;
    Object.defineProperty(obj, prop, {
      ...descriptor,
      get() {
        onInterceptedOperation("Getter:" + prop);

        if (!interceptValue) {
          const baseValue = (descriptor?.get as any).call(obj);
          interceptValue = interceptor(baseValue);
        }

        return interceptValue;
      },
    });
  }

  const idbFactoryMethods = {
    _name: "IDBFactory",
    open: (v: any) => createFunctionProxy(v, "open"),
  };

  const idbOpenDBRequestMethods = {
    _name: "IDBOpenDBRequest",
    result: createProxy,
  };

  const idbDatabaseMethods = {
    _name: "IDBDatabase",
    transaction: (v: any) => createFunctionProxy(v, "transaction"),
  };

  const idbTransactionMethods = {
    _name: "IDBTransaction",
    objectStore: (v: any) => createFunctionProxy(v, "objectStore"),
  };

  function pushIndexedDBAccess(
    request: IDBRequest,
    kind: IndexedDBAccess["kind"],
    key: any,
    item: any
  ) {
    addIndexedDBAccess({
      kind,
      key,
      item,
      storeName: (request.source as any).name,
      databaseName: (request.transaction as any).db.name,
      databaseVersion: (request.transaction as any).db.version,
    });
  }

  // Map "get" requests to their keys.
  const getRequestKeys: Map<IDBRequest, any> = new Map();

  const idbObjectStoreMethods = {
    _name: "IDBObjectStore",
    get: (v: any) =>
      createFunctionProxy(v, "get", (request, key) => {
        // Wait to add the request until the value is known.
        getRequestKeys.set(request, key);
        return createProxy(request);
      }),
    put: (v: any) =>
      createFunctionProxy(v, "put", (request, item, key) => {
        pushIndexedDBAccess(request, "put", key, item);
        return createProxy(request);
      }),
    add: (v: any) =>
      createFunctionProxy(v, "add", (request, item, key) => {
        pushIndexedDBAccess(request, "add", key, item);
        return createProxy(request);
      }),
  };

  const idbRequestMethods = {
    _name: "IDBRequest",
    result: (value: any, target: any) => {
      const key = getRequestKeys.get(target);

      if (key) {
        pushIndexedDBAccess(target, "get", key, value);
      }

      return value;
    },
  };

  function pushLocalStorageAccess(
    kind: LocalStorageAccess["kind"],
    key: string,
    value?: string
  ) {
    addLocalStorageAccess({ kind, key, value });
  }

  const storageMethods = {
    _name: "Storage",
    getItem: (v: any) =>
      createFunctionProxy(v, "getItem", (value: string, key: string) => {
        pushLocalStorageAccess("get", key, value);
        return value;
      }),
    setItem: (v: any) =>
      createFunctionProxy(v, "setItem", (_rv: undefined, key: string) => {
        pushLocalStorageAccess("set", key);
      }),
  };

  // Map Response to the original request index (before redirects).
  const responseToRequestIndex = new WeakMap<Response, number>();

  function convertHeaders(headers: Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  }

  const responseMethods = {
    _name: "Response",
    json: (v: any, response: Response) =>
      createFunctionProxy(
        response.text.bind(response),
        "json",
        async (promise: Promise<any>) => {
          const text = await promise;
          const requestIndex = responseToRequestIndex.get(response);
          const responseHeaders = convertHeaders(response.headers);

          if (requestIndex !== undefined) {
            addTextNetworkResponse(requestIndex, text, response.status, responseHeaders);
          }

          let json;
          try {
            json = JSON.parse(text);
          } catch (e) {
            const request = requestIndex !== undefined ? getNetworkRequest(requestIndex) : undefined;
            if (request) {
              pushDetectedError(
                "Network request not JSON",
                `${request.url} did not return valid JSON`,
                {
                  kind: "network-request-not-json",
                  url: request.url,
                  requestId: getBackendRequestId(responseHeaders),
                  responseBody: text,
                }
              );
            }
            throw e;
          }

          return json;
        }
      ),
    text: (v: any, response: Response) =>
      createFunctionProxy(v, "text", async (promise: Promise<any>) => {
        const text = await promise;
        const requestIndex = responseToRequestIndex.get(response);

        if (requestIndex !== undefined) {
          addTextNetworkResponse(
            requestIndex,
            text,
            response.status,
            convertHeaders(response.headers)
          );
        }

        return text;
      }),
  };

  function createProxy(obj: any) {
    let methods;

    if (obj instanceof IDBFactory) {
      methods = idbFactoryMethods;
    } else if (obj instanceof IDBOpenDBRequest) {
      methods = idbOpenDBRequestMethods;
    } else if (obj instanceof IDBDatabase) {
      methods = idbDatabaseMethods;
    } else if (obj instanceof IDBTransaction) {
      methods = idbTransactionMethods;
    } else if (obj instanceof IDBObjectStore) {
      methods = idbObjectStoreMethods;
    } else if (obj instanceof IDBRequest) {
      methods = idbRequestMethods;
    } else if (obj instanceof Storage) {
      methods = storageMethods;
    } else if (obj instanceof Response) {
      methods = responseMethods;
    }

    assert(methods, "Unknown object for createProxy");

    const name = methods._name;

    return new Proxy(obj, {
      get(target, prop) {
        onInterceptedOperation("ProxyGetter:" + name + "." + String(prop));

        let value = target[prop];

        if (typeof value === "function") {
          value = value.bind(target);
        }

        if (methods[prop]) {
          value = methods[prop](value, target);
        }

        return value;
      },

      set(target, prop, value) {
        onInterceptedOperation("ProxySetter:" + name + "." + String(prop));
        target[prop] = value;

        return true;
      },
    });
  }

  function createFunctionProxy(
    fn: any,
    name: string,
    handler?: (v: any, ...args: any[]) => any
  ) {
    return (...args: any[]) => {
      onInterceptedOperation("FunctionCall:" + name);

      const v = fn(...args);

      return handler ? handler(v, ...args) : createProxy(v);
    };
  }

  interceptProperty(window, "indexedDB", createProxy);
  interceptProperty(window, "localStorage", createProxy);

  const baseFetch = window.fetch;

  window.fetch = async (info, options) => {
    const fetchURL = info instanceof Request ? info.url : info.toString();
    const url = new URL(fetchURL, window.location.href).href;
    const requestBody = typeof options?.body == "string" ? options.body : "";
    const requestBodyBase64 = stringToBase64(requestBody);

    const requestIndex = simulationData.length + 1;
    const request: NetworkRequest = { requestIndex, url, requestBodyBase64, time: new Date().toISOString() };

    addNetworkRequest(request);

    try {
      const rv = await baseFetch(info, options);
      responseToRequestIndex.set(rv, requestIndex);

      return createProxy(rv);
    } catch (error) {
      addNetworkResponse({
        requestIndex,
        error: String(error),
      });
      throw error;
    }
  };

  (window as any).__Nut_onReactError__ = (error: string, componentStack: string) => {
    pushDetectedError("React component exception", error, {
      kind: "react-exception",
      error,
      componentStack,
    });
  };

  getSimulationDataDirect = getSimulationData;
}
